import { CustomMenuListCode } from '@/constants/custom-menu-list-code';
import { EVENT_BENEFIT_CATEGORY_CODES, EVENT_CATEGORY_CODE_SET, EVENT_ENTRY_CATEGORIES, type EventBenefitCategoryCode, type EventEntryCategory } from '@/constants/event-categories';
import { BuildingMenuType } from '@/enums';
import i18n from '@/locales';
import type { BuildingMenu } from '@/services/dto/kiosk-config';
import type { DutyFreeEventApiItem } from '@/services/dto/event';
import type { languageType } from '@/stores/types/configStore';
import type { EventDetailData, EventListItem } from '@/types/event';
import type { ListTypeListItem } from '@/types/list-type';
import { getUploadsUrl } from '@/utils/s3Config';

function getOngoingPeriodLabel(): string {
    return i18n.t('event.period.ongoing');
}

const LANGUAGE_TO_API_LANG: Record<string, string> = {
    ko: 'KO',
    en: 'EN',
    ja: 'JA',
    'zh-CN': 'ZH',
    'zh-TW': 'ZH',
};

const STORE_CODE_TO_SITE_BRCH_CD: Record<string, string> = {
    '0001': '1', // 명동본점
    '0002': '3', // 월드몰
    '0005': '5', // 부산점
};

/** 제주점: storeCode = null → siteBrchCd '6' */
const DEFAULT_SITE_BRCH_CD = '6';

export function resolveSiteBrchCd(storeCode: string | null | undefined): string {
    if (!storeCode) return DEFAULT_SITE_BRCH_CD;
    return STORE_CODE_TO_SITE_BRCH_CD[storeCode] ?? DEFAULT_SITE_BRCH_CD;
}

type ParsedDate = { year: number; month: number; day: number };

function isEmptyPeriodDate(value: string | null | undefined): boolean {
    return value == null || value === '';
}

function isValidParsedDate(date: ParsedDate): boolean {
    return date.year !== 0 && date.month !== 0 && date.day !== 0;
}

function parseApiEventDtime(dtime: string | undefined): ParsedDate {
    if (!dtime || dtime.length < 8) {
        return { year: 0, month: 0, day: 0 };
    }

    return {
        year: parseInt(dtime.slice(0, 4), 10),
        month: parseInt(dtime.slice(4, 6), 10),
        day: parseInt(dtime.slice(6, 8), 10),
    };
}

/** Parse numeric API datetime or `YYYY-MM-DD` / `YYYY/MM/DD` for TTS. */
function parseSpokenDateValue(value: string | null | undefined): ParsedDate | null {
    if (value == null || value === '') return null;

    const trimmed = value.trim();
    if (isNumericDateString(trimmed)) {
        const parsed = parseApiEventDtime(trimmed);
        return isValidParsedDate(parsed) ? parsed : null;
    }

    const match = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (!match) return null;

    const parsed = {
        year: parseInt(match[1], 10),
        month: parseInt(match[2], 10),
        day: parseInt(match[3], 10),
    };
    return isValidParsedDate(parsed) ? parsed : null;
}

/** EN: January…December via common.date.month; other langs: numeric month (no leading zero). */
function getSpokenMonthLabel(month: number): string | number {
    if (i18n.language === 'en') {
        return i18n.t(`common.date.month.${month}`);
    }
    return month;
}

function getSpokenStartParams(start: ParsedDate) {
    return {
        year: start.year,
        month: getSpokenMonthLabel(start.month),
        day: start.day,
    };
}

function formatSpokenPeriodFromParts(start: ParsedDate, end: ParsedDate | null): string {
    if (!end) {
        return i18n.t('event.period.tts.ongoing', getSpokenStartParams(start));
    }

    return i18n.t('event.period.tts.range', {
        ...getSpokenStartParams(start),
        year2: end.year,
        month2: getSpokenMonthLabel(end.month),
        day2: end.day,
    });
}

function formatSpokenPeriodUntilSuppliesLast(start: ParsedDate): string {
    return i18n.t('event.period.tts.supplies_last', getSpokenStartParams(start));
}

export function formatEventDateRange(startDate: string | undefined, endDate: string | undefined): string {
    return getEventPeriodSpokenLabel(startDate, endDate);
}

/**
 * Spoken period for TTS (list card + detail):
 * - endDate null/undefined/'' → ongoing template (requires parseable start)
 * - endDate parseable number/ISO date → range template
 * - endDate free text → supplies-last template (start date + while supplies last)
 *
 * EN months via common.date.month; day/month numbers have no leading zero.
 */
export function getEventPeriodSpokenLabel(
    start: string | null | undefined,
    end: string | null | undefined
): string {
    const startDate = parseSpokenDateValue(start);
    if (!startDate) return '';

    // null / undefined / empty → ongoing
    if (isEmptyPeriodDate(end)) {
        return formatSpokenPeriodFromParts(startDate, null);
    }

    const endDate = parseSpokenDateValue(end);
    if (endDate) {
        return formatSpokenPeriodFromParts(startDate, endDate);
    }

    // Free-text end (e.g. "While supplies last")
    return formatSpokenPeriodUntilSuppliesLast(startDate);
}

export function shouldShowEventPeriod(item: EventListItem): boolean {
    const formatted = getEventPeriodSpokenLabel(item.startDtime, item.endDtime);
    if (!formatted) return false;
    if (isEmptyPeriodDate(item.endDtime)) return false;
    return true;
}

export function shouldShowEventDetailPeriod(detail: EventDetailData): boolean {
    const formatted = getEventPeriodSpokenLabel(detail.eventDateBegin, detail.eventDateEnd);
    if (!formatted) return false;
    if (isEmptyPeriodDate(detail.eventDateEnd)) return false;
    return true;
}

export function formatEventListDateRange(startDtime: string | undefined, endDtime: string | undefined): string {
    const format = (dtime: string | undefined) => {
        if (!dtime || dtime.length < 8) return '';
        return `${dtime.slice(0, 4)}-${dtime.slice(4, 6)}-${dtime.slice(6, 8)}`;
    };

    const start = format(startDtime);
    const end = format(endDtime);
    if (!start && !end) return '';
    if (start && !end) return start;
    if (!start && end) return end;
    return `${start} ~ ${end}`;
}

/** Numeric API datetime like `202605020000` or `20260502` */
function isNumericDateString(value: string): boolean {
    return /^\d{8,}$/.test(value.trim());
}

function formatNumericDateString(value: string): string {
    const digits = value.trim();
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
}

function formatPeriodValue(value: string | null | undefined): string {
    if (value == null) return '';
    if (isNumericDateString(value)) return formatNumericDateString(value);
    return value;
}

/**
 * Period display (config content / list):
 * - null/undefined both → empty
 * - numeric datetime string → `YYYY-MM-DD`
 * - any other text → display as-is
 * - end null/empty with start → `{start} ~ {ongoing}`
 */
export function getRawPeriodText(startDtime: string | null | undefined, endDtime: string | null | undefined): string {
    if (startDtime == null && endDtime == null) return '';

    const start = formatPeriodValue(startDtime);
    const end = formatPeriodValue(endDtime);

    if (!start && !end) return '';
    if (!start) return end;
    if (!end) return `${start} ~ ${getOngoingPeriodLabel()}`;
    return `${start} ~ ${end}`;
}

export function getEventCardAriaLabel(item: EventListItem): string {
    const subtitle = item.subtitle?.trim() && item.subtitle.trim() !== item.title?.trim() ? item.subtitle.trim() : '';
    const period = getEventPeriodSpokenLabel(item.startDtime, item.endDtime);

    return [item.title, subtitle, period].filter(Boolean).join(' ');
}

export function findEventMenu(
    buildingMenus: Record<string, BuildingMenu[]> | null | undefined,
    language: languageType | string
): BuildingMenu | undefined {
    const menus = buildingMenus?.[language] ?? [];
    return menus.find((menu) => menu.menuType === BuildingMenuType.EVENT || menu.menuCode === CustomMenuListCode.EVENT);
}

export function findEventCustomContentByEvtDispNo(
    evtDispNo: string,
    buildingMenus: Record<string, BuildingMenu[]> | null | undefined,
    language: languageType | string
): ListTypeListItem | undefined {
    const eventMenu = findEventMenu(buildingMenus, language);
    return eventMenu?.customContent?.find((item) => item.contentCode === evtDispNo);
}

export function mapCustomContentToEventDetail(item: ListTypeListItem): EventDetailData {
    return {
        title: item.title ?? '',
        contents: item.contents ?? '',
        imageUrl: getUploadsUrl(item.thumbnailUrl?.url),
        eventDateBegin: item.eventDateBegin || undefined,
        eventDateEnd: item.eventDateEnd ?? undefined,
    };
}

export function hasEventDetail(
    evtDispNo: string,
    buildingMenus: Record<string, BuildingMenu[]> | null | undefined,
    language: languageType | string
): boolean {
    return Boolean(findEventCustomContentByEvtDispNo(evtDispNo, buildingMenus, language));
}

export function isEventClickable(
    item: EventListItem,
    buildingMenus?: Record<string, BuildingMenu[]> | null,
    language?: languageType | string
): boolean {
    if (!buildingMenus || !language) return false;
    return hasEventDetail(item.id, buildingMenus, language);
}

export function mapDutyFreeEventToListItem(event: DutyFreeEventApiItem): EventListItem | null {
    if (!EVENT_CATEGORY_CODE_SET.has(event.evtDspAreaCd)) {
        return null;
    }

    return {
        id: event.evtDispNo,
        title: event.dispTit,
        subtitle: event.dispSubTit,
        imageUrl: event.evtMainImg,
        startDtime: event.evtStrtDtime,
        endDtime: event.evtEndDtime,
        langCd: event.langCd,
        benefitCategoryCode: event.evtDspAreaCd as EventBenefitCategoryCode,
        siteBrchCd: event.siteBrchCd,
    };
}

export function mapDutyFreeEvents(events: DutyFreeEventApiItem[] | undefined | null): EventListItem[] {
    if (!events?.length) return [];
    return events.map(mapDutyFreeEventToListItem).filter((item): item is EventListItem => item !== null);
}

export function resolveApiLangCode(language: languageType | string): string {
    return LANGUAGE_TO_API_LANG[language] ?? language.toUpperCase();
}

/**
 * Hide events whose end datetime is a parseable calendar date before today.
 * Plain-text / empty / unparseable `endDtime` values are kept.
 */
export function isEventEndedByPastEndDate(endDtime: string | null | undefined): boolean {
    const endDate = parseSpokenDateValue(endDtime);
    if (!endDate) return false;

    const now = new Date();
    const end = new Date(endDate.year, endDate.month - 1, endDate.day);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return end < today;
}

export function filterEventsByCategoryAndLanguage(
    items: EventListItem[],
    categoryCode: EventBenefitCategoryCode,
    language: languageType | string,
    storeCode?: string | null
): EventListItem[] {
    const apiLang = resolveApiLangCode(language);
    const targetSiteBrchCd = resolveSiteBrchCd(storeCode);

    return items.filter((item) => {
        if (item.benefitCategoryCode !== categoryCode) return false;
        if (item.langCd.toUpperCase() !== apiLang) return false;
        if (isEventEndedByPastEndDate(item.endDtime)) return false;

        if (categoryCode === EVENT_BENEFIT_CATEGORY_CODES.GENERAL) {
            if (!item.siteBrchCd?.includes(targetSiteBrchCd)) return false;
        }

        return true;
    });
}

export function getAvailableEventCategories(
    items: EventListItem[],
    language: languageType | string,
    storeCode?: string | null
): EventEntryCategory[] {
    return EVENT_ENTRY_CATEGORIES.filter(
        (category) => filterEventsByCategoryAndLanguage(items, category.code, language, storeCode).length > 0
    );
}

export function paginateEvents<T>(items: T[], pageSize: number): T[][] {
    const pages: T[][] = [];
    for (let i = 0; i < items.length; i += pageSize) {
        pages.push(items.slice(i, i + pageSize));
    }
    return pages.length > 0 ? pages : [[]];
}
